import React, { useEffect, useState, useRef } from 'react';
import { Mitarbeiter, Projekt, Zuweisung, Abwesenheit, Abteilung } from '../types';
import { ProjectBlock } from './ProjectBlock';
import { cn } from '../lib/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';
interface ResourceMatrixProps {
  mitarbeiter: Mitarbeiter[];
  projekte: Projekt[];
  zuweisungen: Zuweisung[];
  abwesenheiten: Abwesenheit[];
  startKw: number;
  endKw: number;
  selectedAbteilungen: Abteilung[];
  filterProjektId?: string;
  currentKw?: number;
  onEmployeeClick: (employee: Mitarbeiter) => void;
  onProjectClick: (project: Projekt) => void;
  onKwClick?: (kw: number) => void;
}
export function ResourceMatrix({
  mitarbeiter,
  projekte,
  zuweisungen,
  abwesenheiten,
  startKw,
  endKw,
  selectedAbteilungen,
  filterProjektId,
  currentKw = 7,
  onEmployeeClick,
  onProjectClick,
  onKwClick
}: ResourceMatrixProps) {
  const WEEK_WIDTH = 48;
  const ROW_HEIGHT = 56;
  const [collapsedDepts, setCollapsedDepts] = useState<Record<string, boolean>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const toggleDept = (dept: string) => {
    setCollapsedDepts(prev => ({
      ...prev,
      [dept]: !prev[dept]
    }));
  };
  const weeks = Array.from({
    length: endKw - startKw + 1
  }, (_, i) => startKw + i);
  // Scroll to current KW on mount and when currentKw changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      const offset = (currentKw - startKw) * WEEK_WIDTH - 200; // Center-ish
      scrollContainerRef.current.scrollLeft = Math.max(0, offset);
    }
  }, [currentKw, startKw]);
  // Sync horizontal scroll between header and body
  const handleBodyScroll = () => {
    if (scrollContainerRef.current && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
    }
  };
  // Group employees by department
  const groupedMitarbeiter = mitarbeiter.filter(m => selectedAbteilungen.includes(m.abteilung)).reduce((acc, m) => {
    if (!acc[m.abteilung]) acc[m.abteilung] = [];
    acc[m.abteilung].push(m);
    return acc;
  }, {} as Record<Abteilung, Mitarbeiter[]>);
  const isHoliday = (kw: number) => [1, 13, 21, 52].includes(kw);
  const deptColors: Record<Abteilung, string> = {
    Konstruktion: 'border-l-blue-500',
    'E-Konstruktion': 'border-l-green-500',
    Montage: 'border-l-orange-500',
    Einkauf: 'border-l-purple-500',
    Inbetriebnahme: 'border-l-teal-500'
  };
  const handleKwClick = (kw: number) => {
    if (onKwClick) onKwClick(kw);
  };
  return <div className="flex flex-col h-full border rounded-xl overflow-hidden bg-background shadow-sm ring-1 ring-border/50" data-id="element-334">
      {/* Header Row (Weeks) */}
      <div className="flex border-b bg-muted/5 backdrop-blur-sm z-20" data-id="element-335">
        <div className="w-72 flex-shrink-0 p-3 font-semibold border-r flex items-center bg-background/95 backdrop-blur sticky left-0 z-30 shadow-[4px_0_12px_-4px_rgba(0,0,0,0.1)]" data-id="element-336">
          <span className="text-sm text-muted-foreground uppercase tracking-wider font-bold" data-id="element-337">
            Mitarbeiter
          </span>
        </div>
        <div className="flex-1 overflow-hidden" ref={headerScrollRef} data-id="element-338">
          <div className="flex relative" data-id="element-339">
            {weeks.map(kw => {
            const isCurrent = kw === currentKw;
            return <div key={kw} className={cn('flex-shrink-0 border-r border-border/40 text-center text-xs py-3 font-medium relative transition-colors cursor-pointer', isHoliday(kw) ? 'bg-red-50/30 text-red-600' : 'bg-background/50 hover:bg-muted/30', isCurrent ? 'bg-primary/5 text-primary font-bold' : 'text-muted-foreground', kw % 4 === 0 ? 'border-r-border/80' : '')} style={{
              width: WEEK_WIDTH
            }} onClick={() => handleKwClick(kw)} data-id="element-340">
                  <span className={cn('inline-block px-1.5 py-0.5 rounded transition-colors', isCurrent && 'bg-primary text-primary-foreground shadow-sm', !isCurrent && 'hover:bg-muted')} data-id="element-341">
                    KW {kw}
                  </span>
                  {isCurrent && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-primary/30" data-id="element-342" />}
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Body (Scrollable) */}
      <div className="flex-1 overflow-auto custom-scrollbar relative" ref={scrollContainerRef} onScroll={handleBodyScroll} data-id="element-343">
        {/* Current Week Highlight Column Background */}
        <div className="absolute top-0 bottom-0 bg-primary/5 pointer-events-none z-0 border-x border-primary/10" style={{
        left: 288 + (currentKw - startKw) * WEEK_WIDTH,
        width: WEEK_WIDTH
      }} data-id="element-344" />

        {Object.entries(groupedMitarbeiter).map(([abteilung, employees]) => <div key={abteilung} className="relative z-10" data-id="element-345">
            {/* Department Header */}
            <div className={cn('sticky left-0 z-20 bg-muted/90 backdrop-blur-md px-4 py-2 text-xs font-bold uppercase tracking-wider border-y border-border/60 text-foreground flex items-center gap-2 shadow-sm cursor-pointer hover:bg-muted transition-colors select-none', deptColors[abteilung as Abteilung])} onClick={() => toggleDept(abteilung)} data-id="element-346">
              {collapsedDepts[abteilung] ? <ChevronRight className="h-3 w-3" data-id="element-347" /> : <ChevronDown className="h-3 w-3" data-id="element-348" />}
              <div className={cn('w-2 h-2 rounded-full', deptColors[abteilung as Abteilung].replace('border-l-', 'bg-'))} data-id="element-349" />
              {abteilung}
              <span className="bg-background/50 px-1.5 py-0.5 rounded text-[10px] ml-auto border border-border/50 text-muted-foreground" data-id="element-350">
                {employees.length}
              </span>
            </div>

            {!collapsedDepts[abteilung] && employees.map((employee, idx) => {
          const employeeAssignments = zuweisungen.filter(z => z.mitarbeiterId === employee.id && (!filterProjektId || z.projektId === filterProjektId));
          const employeeAbsences = abwesenheiten.filter(a => a.mitarbeiterId === employee.id);
          const isEven = idx % 2 === 0;
          return <div key={employee.id} className={cn('flex border-b border-border/40 transition-colors group relative', isEven ? 'bg-background' : 'bg-muted/5', 'hover:bg-muted/20')} style={{
            height: ROW_HEIGHT
          }} data-id="element-351">
                    {/* Employee Name Column */}
                    <div className={cn('w-72 flex-shrink-0 px-4 border-r border-border/60 flex items-center gap-3 sticky left-0 z-20 shadow-[4px_0_12px_-4px_rgba(0,0,0,0.05)] transition-colors cursor-pointer', isEven ? 'bg-background/95' : 'bg-muted/10 backdrop-blur-sm', 'group-hover:bg-muted/20 hover:text-primary')} onClick={() => onEmployeeClick(employee)} data-id="element-352">
                      <div className="relative" data-id="element-353">
                        <div className="h-9 w-9 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground text-xs font-bold shadow-sm group-hover:border-primary/50 transition-colors" data-id="element-354">
                          {employee.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {employee.verfuegbarkeit < 100 && <div className="absolute -bottom-1 -right-1 bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800 text-[9px] px-1 rounded-full font-bold shadow-sm" data-id="element-355">
                            {employee.verfuegbarkeit}%
                          </div>}
                      </div>

                      <div className="min-w-0 flex-1" data-id="element-356">
                        <div className="text-sm font-medium truncate text-foreground group-hover:text-primary transition-colors" data-id="element-357">
                          {employee.name}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate flex gap-1 mt-0.5" data-id="element-358">
                          {employee.skills.slice(0, 2).map(skill => <span key={skill} className="bg-muted px-1 rounded-[3px] border border-border/50" data-id="element-359">
                              {skill}
                            </span>)}
                          {employee.skills.length > 2 && <span className="bg-muted px-1 rounded-[3px] border border-border/50" data-id="element-360">
                              +{employee.skills.length - 2}
                            </span>}
                        </div>
                      </div>
                    </div>

                    {/* Timeline Columns */}
                    <div className="flex relative" data-id="element-361">
                      {weeks.map(kw => {
                const isAbsence = employeeAbsences.some(a => kw >= a.startKw && kw < a.endKw);
                return <div key={kw} className={cn('flex-shrink-0 border-r border-border/30 h-full transition-colors', isHoliday(kw) ? 'bg-stripes-red bg-red-50/20' : '', isAbsence ? 'bg-stripes-gray bg-muted/20' : '', kw % 4 === 0 ? 'border-r-border/60' : '')} style={{
                  width: WEEK_WIDTH
                }} data-id="element-362">
                            {isAbsence && <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-363">
                                <span className="text-[9px] font-bold text-muted-foreground/50 -rotate-45 select-none" data-id="element-364">
                                  ABW
                                </span>
                              </div>}
                          </div>;
              })}

                      {/* Render Assignment Blocks Overlay */}
                      {employeeAssignments.map(assignment => {
                const project = projekte.find(p => p.id === assignment.projektId);
                if (!project) return null;
                const isOverbooked = employeeAssignments.some(other => other.id !== assignment.id && Math.max(assignment.startKw, other.startKw) < Math.min(assignment.endKw, other.endKw));
                return <ProjectBlock key={assignment.id} projekt={project} zuweisung={assignment} weekWidth={WEEK_WIDTH} startOffset={startKw} isOverbooked={isOverbooked} onClick={() => onProjectClick(project)} data-id="element-365" />;
              })}
                    </div>
                  </div>;
        })}
          </div>)}
      </div>
    </div>;
}